import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAppSelector } from "../../app/hooks";
import { botIcon, speakerIcon, userIcon, activeSpeakerIcon } from "../../assets/icons";
import { askDidiLogo } from "../../assets/images";
import { handleSpeechSynthesis, initVoices } from "../../utils/handleSpeechSynthesis";
import { useAppDispatch } from "../../app/hooks";
import { setQuery } from "../../features/queryAnsSlice";
import { fetchAnswerMiddleware } from "../../middlewares/fetchAnswerMiddleware";
import { utils, writeFile } from 'xlsx';
import downloadIcon from "../../assets/icons/download.svg";

import StreamingText from "./StreamingText";
import "./queryAns.css";



const stripHtmlTags = (html) => {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
};

const isTabularData = (content) => {
    return content && content.toLowerCase().includes('<table');
};

const highlightKeywords = (text) => {
    return text; // Highlighting disabled per user request
};

const initialText = `नमस्ते , मैं स्मार्ट दीदी हूं, आपकी Jeevika चैटबॉट। मैं Jeevika और इसके थीम पर डिजिटल सॉल्यूशन से संबंधित प्रश्नों का उत्तर देने के लिए प्रशिक्षित हूं, जिसमें IBCB, वित्तीय समावेशन, कृषि, गैर-कृषि, पशुधन, नौकरियां, HNS, सामाजिक विकास आदि शामिल हैं। यदि आपके पास इन विषयों पर कोई प्रश्न हैं, तो कृपया पूछने में संकोच न करें।`;

const loadingMessages = [
    "Analyzing your query",
    "Searching database",
    "Consulting context",
    "Applying business rules",
    "Structuring response"
];

function QueryAns(props) {
    const [isSpeakerClicked, setIsSpeakerClicked] = useState({});
    const [contentType, setContentType] = useState('');
    const query = useAppSelector((state) => state.queryAnsReducer.query);
    const answer = useAppSelector((state) => state.queryAnsReducer.ans);
    const loading = useAppSelector((state) => state.queryAnsReducer.loading);
    const apiSuggestedQuestions = useAppSelector((state) => state.queryAnsReducer.suggestedQuestions);
    const dispatch = useAppDispatch();

    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

    const [isStreamingPaused, setIsStreamingPaused] = useState({});
    const [hasCompletedStreaming, setHasCompletedStreaming] = useState({});
    const audioQueueRef = useRef([]);
    const isProcessingQueueRef = useRef(false);
    const currentAudioRef = useRef(null);
    const stopPlaybackRef = useRef(false);

    useEffect(() => {
        initVoices();
    }, []);

    const processNextInQueue = useCallback(async () => {
        if (audioQueueRef.current.length === 0 || stopPlaybackRef.current) {
            isProcessingQueueRef.current = false;
            stopPlaybackRef.current = false;
            return;
        }

        isProcessingQueueRef.current = true;
        const { htmlContent, index, contentTypeIS } = audioQueueRef.current.shift();

        try {
            // Reset previous speaker icons and set current one
            setIsSpeakerClicked({ [index]: true });
            setContentType(contentTypeIS);

            const plainText = stripHtmlTags(htmlContent);

            // Use Web Speech API
            const utterance = handleSpeechSynthesis(plainText, () => {
                // On End Callback
                setIsSpeakerClicked(prev => ({
                    ...prev,
                    [index]: false
                }));
                currentAudioRef.current = null;
                processNextInQueue();
            });

            if (utterance) {
                currentAudioRef.current = utterance;

                // Handle start event to manage streaming pause state if needed
                utterance.onstart = () => {
                    setIsStreamingPaused(prev => ({
                        ...prev,
                        [`${index}-${contentTypeIS}`]: false
                    }));
                };
            } else {
                setIsSpeakerClicked(prev => ({ ...prev, [index]: false }));
                processNextInQueue();
            }

        } catch (error) {
            console.error("TTS Error in queue:", error);
            setIsSpeakerClicked(prev => ({ ...prev, [index]: false }));
            processNextInQueue();
        }
    }, []); // Stable dependencies

    const handleSpeakerClick = useCallback(async (htmlContent, index, contentTypeIS) => {
        const isCurrentPlaying = isSpeakerClicked[index] && contentType === contentTypeIS;

        if (isCurrentPlaying) {
            // STOP functionality: If clicking the active speaker, stop everything
            if (currentAudioRef.current) {
                window.speechSynthesis.cancel();
                currentAudioRef.current = null;
            }
            audioQueueRef.current = [];
            stopPlaybackRef.current = true;
            setIsSpeakerClicked({});
            isProcessingQueueRef.current = false;
            return;
        }

        // QUEUE functionality: If clicking a new speaker, add to queue
        audioQueueRef.current.push({ htmlContent, index, contentTypeIS });

        if (!isProcessingQueueRef.current) {
            stopPlaybackRef.current = false;
            processNextInQueue();
        }
    }, [isSpeakerClicked, contentType, processNextInQueue]);

    const handleSuggestedQuestionClick = (question) => {
        dispatch(setQuery(question));
        dispatch(fetchAnswerMiddleware(question));
    };

    const handleStreamingComplete = useCallback((id) => {
        setHasCompletedStreaming(prev => ({
            ...prev,
            [id]: true
        }));
    }, []);

    /* Auto-play disabled per user request. Manual play via speaker icon only. */

    const handleDownload = (htmlContent) => {
        try {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = htmlContent;
            const tableElement = tempDiv.querySelector("table");

            if (tableElement) {
                // Properly parse the HTML table element
                const worksheet = utils.table_to_sheet(tableElement);
                const workbook = utils.book_new();
                utils.book_append_sheet(workbook, worksheet, 'Sheet1');
                writeFile(workbook, 'jeevika_data.xlsx');
            } else {
                // Fallback for cases where it's not a table structure but still "tabular"
                const plainText = stripHtmlTags(htmlContent);
                const worksheet = utils.json_to_sheet([{ Response: plainText }]);
                const workbook = utils.book_new();
                utils.book_append_sheet(workbook, worksheet, 'Sheet1');
                writeFile(workbook, 'jeevika_data.xlsx');
            }
        } catch (error) {
            console.error("Download failed:", error);
            alert("Failed to generate Excel file. Please try again.");
        }
    };





    useEffect(() => {
        let interval;
        if (loading) {
            setLoadingMessageIndex(0);
            interval = setInterval(() => {
                setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
            }, 2500); // Change message every 2.5 seconds
        }
        return () => clearInterval(interval);
    }, [loading]);

    // Use a dummy div at end of list for reliable scrolling
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        }
    };

    useEffect(() => {
        // Scroll on mount, update, AND stream updates
        // Use multiple timeouts to catch layout shifts
        scrollToBottom();

        requestAnimationFrame(() => {
            scrollToBottom();
        });

        const t1 = setTimeout(scrollToBottom, 100);
        const t2 = setTimeout(scrollToBottom, 500); // For slower renders/images

        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
        };
    }, [query, answer, loading, apiSuggestedQuestions]);

    return (
        <div id={props.id} className="query-ans-scroll-container">
            {/* Fetch and display query and answer in conversation window */}
            {true && (
                <div className="ans">
                    <img
                        className="bot-icon conversation-profile"
                        src={botIcon}
                        alt={askDidiLogo}
                    />
                    <div className="ans-conversation-txt-container">
                        <StreamingText
                            text={initialText}
                            highlightFn={highlightKeywords}
                            paused={isStreamingPaused['initial-welcome'] ?? false}
                            onComplete={() => handleStreamingComplete('initial')}
                        />
                        {/* No suggestions with initial welcome text */}
                        <img
                            className="speaker"
                            src={isSpeakerClicked['initial'] ? activeSpeakerIcon : speakerIcon}
                            alt="speaker"
                            onClick={() => {
                                handleSpeakerClick(initialText, 'initial', 'welcome');
                            }}
                        />
                    </div>
                </div>
            )}
            {/* Render user queries and bot answers */}
            {query.map((question, index) => {
                const ans = answer[index];

                return (
                    <div className="queryAns-container" key={index}>
                        <div className="query">
                            <div className="query-conversation-txt-container">
                                <p className="conversation-txt">{question}</p>
                                <img
                                    className="speaker"
                                    src={
                                        isSpeakerClicked[index] && contentType === 'query' ? activeSpeakerIcon : speakerIcon
                                    }
                                    alt="speaker"
                                    onClick={() => handleSpeakerClick(question, index, 'query')}
                                />
                            </div>
                            <img
                                className="user-icon conversation-profile"
                                src={userIcon}
                                alt={userIcon}
                            />
                        </div>

                        {ans !== undefined ? (
                            <div className="ans">
                                <img
                                    className="bot-icon conversation-profile"
                                    src={botIcon}
                                    alt={askDidiLogo}
                                />
                                <div className="ans-content">
                                    <div className="ans-conversation-txt-container">
                                        <StreamingText
                                            text={ans}
                                            highlightFn={highlightKeywords}
                                            paused={isStreamingPaused[`${index}-answer`] ?? false}
                                            shouldStream={index === answer.length - 1}
                                            onComplete={() => handleStreamingComplete(index)}
                                        />

                                        {/* Speaker Icon and Download Button grouped together */}
                                        <div className="ans-actions">
                                            <img
                                                className="speaker"
                                                src={
                                                    isSpeakerClicked[index] && contentType === 'answer' ? activeSpeakerIcon : speakerIcon
                                                }
                                                alt="speaker"
                                                onClick={() => handleSpeakerClick(ans, index, 'answer')}
                                            />
                                            {isTabularData(ans) && (
                                                <button className="download-btn" onClick={() => handleDownload(ans)}>
                                                    <img src={downloadIcon} alt="Download Icon" className="download-icon" />
                                                    <span className="download-text">Download</span>
                                                </button>
                                            )}
                                        </div>

                                        {hasCompletedStreaming[index] && apiSuggestedQuestions[index] && apiSuggestedQuestions[index].length > 0 && (
                                            <div className="suggested-questions-container">
                                                <p className="suggested-header">
                                                    {[
                                                        "Here are a few things I can help with:",
                                                        "Try these instead:",
                                                        "Would you like to try one of these popular questions?",
                                                        "Try these suggestions:",
                                                        "Try asking me this:",
                                                        "You might also be interested in:",
                                                        "How about these options?",
                                                        "Consider asking:",
                                                        "Related questions you might like:",
                                                        "Explore these topics:"
                                                    ][index % 10]}
                                                </p>
                                                <div className="suggested-questions">
                                                    {apiSuggestedQuestions[index].map((q, i) => (
                                                        <button
                                                            key={i}
                                                            className="suggested-question-btn"
                                                            onClick={() => handleSuggestedQuestionClick(q)}
                                                        >
                                                            {i + 1}. {q}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : loading ? (
                            <div className="ans">
                                <img
                                    className="bot-icon conversation-profile"
                                    src={botIcon}
                                    alt={askDidiLogo}
                                />
                                <div className="ans-conversation-txt-container">
                                    <div className="loading-container">
                                        <div className="scanning-line"></div>
                                        <p className="conversation-txt">
                                            {loadingMessages[loadingMessageIndex]}
                                        </p>
                                        <div className="spinner">
                                            <div className="dot1"></div>
                                            <div className="dot2"></div>
                                            <div className="dot3"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                );
            })}
            {/* Dummy div to scroll to */}
            <div ref={messagesEndRef} className="scroll-anchor" />
        </div>
    );
}

export default QueryAns;
